import * as React from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Calendar as CalendarIcon, X, History, Share2, Camera, Loader2, CheckCircle2, ImageIcon, Search, TrendingUp, Users, Layers } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import type { SessionsFilter, SessionsViewProps } from "@/ui/view-props";
import { FullScreenImageViewer } from "@/components/FullScreenImageViewer";
import { SkeletonRow } from "./SkeletonLoader";

const filters: { label: string; value: SessionsFilter }[] = [
  { label: "All", value: "all" },
  { label: "Unsettled", value: "unsettled" },
  { label: "Settled", value: "settled" },
];

export function SessionsNeonView(p: SessionsViewProps) {
  const targetPrefix = p.isReadOnly ? `/public/${p.targetUserId}` : "";

  return (
    <div className="animate-fade-in flex flex-col gap-6">

      {/* ── Header ──────────────────────────────────────── */}
      <div className="px-1">
        <h1 className="text-2xl font-black text-white tracking-tight uppercase leading-none">
          Sessions
        </h1>
        <div className="text-xs text-neon-muted font-bold tracking-widest mt-1 flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-neon-cyan" />
          HISTORY & PERFORMANCE
        </div>
      </div>

      {/* ── Hero stat strip ──────────────────────────────── */}
      <div className="glass-card grid grid-cols-3 overflow-hidden">
        {[
          { label: "Unsettled", value: p.loading ? "…" : p.stats.unsettled, color: "text-neon-amber", icon: TrendingUp },
          { label: "Players",   value: p.loading ? "…" : p.stats.players,   color: "text-neon-cyan",   icon: Users },
          { label: "Total",     value: p.loading ? "…" : p.stats.totalSessions, color: "text-neon-green", icon: Layers },
        ].map((stat, i) => (
          <div
            key={stat.label}
            className={cn(
              "p-4 flex flex-col items-center gap-1 animate-slide-up",
              i < 2 && "border-r border-white/5"
            )}
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className={cn("text-xl font-black tabular-nums", stat.color)}>
              {stat.value}
            </div>
            <div className="text-[10px] font-black text-neon-muted uppercase tracking-tighter flex items-center gap-1">
              <stat.icon className="w-2.5 h-2.5" />
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Controls ─────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neon-muted" />
          <input
            className="neon-input pl-10 bg-white/5 border-white/10"
            placeholder="Search players or date..."
            value={p.searchQuery}
            onChange={(e) => p.setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-3 overflow-x-auto pb-1 no-scrollbar">
          <div className="neon-tabs p-1 bg-white/5 shrink-0">
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                className={cn(
                  "px-4 py-1.5 rounded-lg text-xs font-black uppercase transition-all",
                  p.filter === f.value 
                    ? "bg-neon-cyan/20 text-neon-cyan shadow-[0_0_12px_hsl(var(--neon-cyan)/0.25)]" 
                    : "text-neon-muted hover:text-white"
                )}
                onClick={() => p.setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {p.settings.session_calendar_search && (
            <div className="flex items-center gap-2 shrink-0">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      "flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
                      p.selectedDate 
                        ? "bg-neon-cyan/20 border border-neon-cyan/40 text-neon-cyan" 
                        : "bg-white/5 border border-white/10 text-neon-muted"
                    )}
                  >
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {p.selectedDate ? format(p.selectedDate, "MMM d") : "Date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 glass border-neon-border" align="start">
                  <Calendar mode="single" selected={p.selectedDate} onSelect={p.setSelectedDate} initialFocus />
                </PopoverContent>
              </Popover>
              {p.selectedDate && (
                <button
                  type="button"
                  className="p-1.5 rounded-lg bg-white/5 text-neon-muted hover:text-white"
                  onClick={() => p.setSelectedDate(undefined)}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── List ─────────────────────────────────────────── */}
      {p.loading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonRow key={i} />)}
        </div>
      ) : p.sessions.length === 0 ? (
        <div className="glass-card p-12 text-center flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
            <History className="w-8 h-8 text-neon-muted opacity-20" />
          </div>
          <div className="text-xs font-black text-neon-muted uppercase tracking-widest">No sessions found</div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {p.sessions.map((s, i) => (
            <div
              key={s.id}
              className="glass-card p-4 flex items-center gap-4 hover:border-neon-cyan/40 animate-slide-up"
              style={{ animationDelay: `${Math.min(i * 30, 300)}ms` }}
            >
              <Link
                to={`${targetPrefix}/session/${s.id}`}
                className="flex-1 min-w-0"
              >
                <div className="text-sm font-black text-white truncate mb-0.5">
                  {format(new Date(s.date), "MMMM d, yyyy")}
                </div>
                <div className="text-[10px] font-bold text-neon-muted uppercase flex items-center gap-2">
                  <span className="text-neon-cyan">{s.session_players?.length || 0} PLAYERS</span>
                  <span className="w-1 h-1 rounded-full bg-white/10" />
                  <span>{format(new Date(s.date), "h:mm a")}</span>
                </div>
              </Link>

              <div className="flex items-center gap-2 shrink-0">
                {p.scoreSheetUrls && p.scoreSheetUrls[s.id] && (
                  <FullScreenImageViewer 
                    src={p.scoreSheetUrls[s.id]} 
                    trigger={
                      <button 
                        type="button" 
                        className="w-8 h-8 rounded-lg overflow-hidden border border-white/10 hover:border-neon-cyan/50 transition-colors"
                      >
                        <img 
                          src={p.scoreSheetUrls[s.id]} 
                          alt="thumbnail" 
                          className="w-full h-full object-cover"
                        />
                      </button>
                    }
                  />
                )}
                
                <div className={cn(
                  "px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border",
                  s.status === "settled"
                    ? "bg-neon-emerald/10 border-neon-emerald/30 text-neon-emerald"
                    : "bg-neon-amber/10 border-neon-amber/30 text-neon-amber"
                )}>
                  {s.status}
                </div>

                {!p.isReadOnly && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      id={`camera-${s.id}`}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) p.onUploadScoreSheet(s.id, file);
                      }}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      id={`gallery-${s.id}`}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) p.onUploadScoreSheet(s.id, file);
                      }}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="p-2 rounded-lg bg-white/5 border border-white/5 hover:border-white/20 transition-colors"
                          disabled={p.isUploading === s.id}
                        >
                          {p.isUploading === s.id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-neon-cyan" />
                          ) : (s.score_sheet_path) ? (
                            <CheckCircle2 className="w-4 h-4 text-neon-emerald" />
                          ) : (
                            <Camera className="w-4 h-4 text-neon-muted" />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="glass border-neon-border text-neon-text">
                        <DropdownMenuItem onClick={() => document.getElementById(`camera-${s.id}`)?.click()} className="focus:bg-neon-cyan/20 focus:text-neon-cyan">
                          <Camera className="mr-2 h-4 w-4" />
                          <span>Take Photo</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => document.getElementById(`gallery-${s.id}`)?.click()} className="focus:bg-neon-emerald/20 focus:text-neon-emerald">
                          <ImageIcon className="mr-2 h-4 w-4" />
                          <span>Photo Library</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}

                <button
                  type="button"
                  className="p-2 rounded-lg bg-white/5 border border-white/5 hover:border-white/20 transition-colors"
                  title="Share on WhatsApp"
                  onClick={(e) => {
                    e.preventDefault();
                    const dateStr = format(new Date(s.date), "MMM d, yyyy");
                    const resultsSummary = [...(s.session_players || [])]
                      .sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))
                      .map((sp, i) => `${i + 1}. ${sp.players?.name}: ${sp.net_amount >= 0 ? '+' : ''}${Math.round(sp.net_amount)}`)
                      .join('\n');

                    const url = `${window.location.origin}${targetPrefix}/session/${s.id}`;
                    const text = `🃏 SSC Session — ${dateStr}\n\n${resultsSummary}\n\nFull results & portal: ${url}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                >
                  <Share2 className="w-4 h-4 text-neon-muted" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
